import { getBaseUrl } from 'markcat-api-client';
import type { User } from 'markcat-domain';
import { useAuthStore } from './auth';

export type SessionOutcome = 'ok' | 'auth_failed' | 'transient';

/** Fetch `/api/auth/me` with the given token and populate the auth store —
 *  the shared tail of both restore paths. Uses raw fetch (not apiFetch) to
 *  keep the redirect decision in the caller's hands. */
async function fetchUserIntoStore(accessToken: string): Promise<SessionOutcome> {
  let me: Response;
  try {
    me = await fetch(`${getBaseUrl()}/api/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    return 'transient';
  }
  if (me.status === 401) return 'auth_failed';
  if (!me.ok) return 'transient';

  try {
    const user: User = await me.json();
    useAuthStore.getState().setAuth(user, accessToken);
    return 'ok';
  } catch {
    return 'transient';
  }
}

/**
 * Silently restore a session from the HttpOnly refresh cookie.
 *
 * The access token lives only in localStorage; the 30-day refresh token lives
 * in the `markcat_refresh` cookie. If localStorage is evicted (Safari ITP,
 * partial site-data clear) while the cookie survives, the user would otherwise
 * be bounced to login despite a valid session. This rebuilds auth from the
 * cookie: refresh → /me → setAuth.
 *
 * Returns a terminal/transient outcome so the caller shows login only on a real
 * 401 and a retry screen on a network blip — never logging out on a blip.
 */
export async function restoreSession(): Promise<SessionOutcome> {
  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return 'transient';
  }
  if (res.status === 401) return 'auth_failed';
  if (!res.ok) return 'transient';

  let accessToken: string;
  try {
    const data = await res.json();
    if (typeof data.access_token !== 'string' || !data.access_token) return 'transient';
    accessToken = data.access_token;
  } catch {
    return 'transient';
  }

  return fetchUserIntoStore(accessToken);
}

/**
 * Rehydrate `user` from `/api/auth/me` when a persisted access token survived
 * a reload but the in-memory user did not (only login and restoreSession ever
 * set it). Maintains the invariant "authenticated ⇒ user populated" at the
 * boundary that owns session hydration. `auth_failed` means the persisted
 * token is stale — the caller falls back to the cookie-refresh restore.
 */
export async function rehydrateUser(): Promise<SessionOutcome> {
  const accessToken = useAuthStore.getState().accessToken;
  if (!accessToken) return 'auth_failed';
  return fetchUserIntoStore(accessToken);
}
